import React from 'react';
import { CalendarIcon, ClockIcon, GlobeIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { EVENT_TYPES } from '../../data/eventSchema';
import type { FullEventData } from '../../data/eventSchema';
interface EventHeaderProps {
  event: FullEventData;
}
export const EventHeader: React.FC<EventHeaderProps> = ({
  event
}) => {
  const dk = useDarkClasses();
  const eventTypeLabel = EVENT_TYPES.find(t => t.value === event.eventType)?.label || event.eventType;
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'upcoming':
        return {
          color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
          label: 'Upcoming'
        };
      case 'tickets-live':
        return {
          color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
          label: 'Tickets Live'
        };
      case 'sold-out':
        return {
          color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
          label: 'Sold Out'
        };
      case 'postponed':
        return {
          color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
          label: 'Postponed'
        };
      case 'cancelled':
        return {
          color: 'bg-red-100 text-red-800 line-through dark:bg-red-900/30 dark:text-red-300',
          label: 'Cancelled'
        };
      case 'completed':
        return {
          color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
          label: 'Completed'
        };
      default:
        return {
          color: 'bg-gray-100 text-gray-800',
          label: status
        };
    }
  };
  const statusConfig = getStatusConfig(event.status);
  const isMultiDay = event.startDate !== event.endDate;
  // Format dates
  const startDateObj = new Date(event.startDate);
  const endDateObj = new Date(event.endDate);
  const dateDisplay = isMultiDay ? `${startDateObj.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric'
  })} - ${endDateObj.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })}` : startDateObj.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
  // Timezone conversion logic (simplified for UI display)
  const formatTime = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };
  // Get country flag emoji
  const getCountryFlag = (country?: string) => {
    if (!country) return '';
    const map: Record<string, string> = {
      'United States': '🇺🇸',
      'United Kingdom': '🇬🇧',
      Canada: '🇨🇦',
      Australia: '🇦🇺',
      'New Zealand': '🇳🇿',
      Ireland: '🇮🇪'
    };
    return map[country] || '🌍';
  };
  return <div className="mb-8" data-id="element-8966" data-mp="340hq1m0b9h81">
      <div className="flex flex-wrap items-center gap-3 mb-4" data-id="element-8967" data-mp="390vxb1u38n50">
        <span className="px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300" data-id="element-8968" data-mp="3926zix7vxmu0">
          {eventTypeLabel}
        </span>
        <span className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full ${statusConfig.color}`} data-id="element-8969" data-mp="302bjadzh31n4">
          {statusConfig.label}
        </span>
        {!event.isVirtual && event.location?.country && <span className={`text-sm font-medium ${dk.textSecondary}`} data-id="element-8970" data-mp="3e0ibpelnk9fe">
            {getCountryFlag(event.location.country)} {event.location.country}
          </span>}
      </div>

      <h1 className={`text-3xl md:text-4xl font-bold mb-6 ${dk.textHeading}`} data-id="element-8971" data-mp="ajjmyr79nwul">
        {event.eventName}
      </h1>

      <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8" data-id="element-8972" data-mp="fa8cw23aslrm">
        <div className={`flex items-center ${dk.textPrimary}`} data-id="element-8973" data-mp="2mtbd62iaatxd">
          <CalendarIcon className="w-5 h-5 mr-2 text-purple-500" data-id="element-8974" data-mp="2muuqbk8n8oux" />
          <span className="font-medium text-lg" data-id="element-8975" data-mp="2htf59s6guicj">{dateDisplay}</span>
        </div>

        <div className="flex flex-col" data-id="element-8976" data-mp="2ds3e3i7y1p28">
          <div className={`flex items-center ${dk.textPrimary}`} data-id="element-8977" data-mp="2dt536qlsoc2y">
            <ClockIcon className="w-5 h-5 mr-2 text-purple-500" data-id="element-8978" data-mp="28utskdnodxjo" />
            <span className="font-medium text-lg" data-id="element-8979" data-mp="35k1cxick60m0">
              {formatTime(event.startTime)} - {formatTime(event.endTime)}
            </span>
            <span className={`ml-2 text-sm ${dk.textMuted}`} data-id="element-8980" data-mp="31jqz5jiiy5wl">
              ({event.timezone.split('/')[1]?.replace('_', ' ')})
            </span>
          </div>
          <div className={`flex items-center mt-1 text-sm ${dk.textFaint} ml-7`} data-id="element-8981" data-mp="2rsw9e562ko6e">
            <GlobeIcon className="w-3 h-3 mr-1" data-id="element-8982" data-mp="3p8jyzr29oxwh" />
            <span data-id="element-8983" data-mp="3p9cw00bfc91f">Auto-adjusts to your local time</span>
          </div>
        </div>
      </div>
    </div>;
};